// Vendor Applications — admin review queue.
// URL: /app/procurement/vendor-applications
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Copy, RefreshCw, Link as LinkIcon, ExternalLink, FileText, ShieldCheck, X } from "lucide-react";

const STATUS_LABELS = {
  submitted: { label: "Submitted", cls: "bg-blue-100 text-blue-700" },
  under_review: { label: "Under review", cls: "bg-amber-100 text-amber-700" },
  clarification_needed: { label: "Clarification needed", cls: "bg-orange-100 text-orange-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-700" },
  draft: { label: "Draft (in progress)", cls: "bg-zinc-100 text-zinc-600" },
};

const TABS = [
  { key: "all",          label: "All" },
  { key: "submitted",    label: "Submitted" },
  { key: "under_review", label: "Under review" },
  { key: "clarification_needed", label: "Clarification" },
  { key: "approved",     label: "Approved" },
  { key: "rejected",     label: "Rejected" },
];

export default function VendorApplications() {
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("submitted");
  const [active, setActive] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/vendor-apps");
      setApps(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load applications"); }
  };
  const loadSettings = async () => {
    try {
      const r = await api.get("/companies/me/vendor-onboarding");
      setSettings(r.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); loadSettings(); }, []);

  const filtered = useMemo(() => apps.filter(a => {
    if (tab !== "all" && a.status !== tab) return false;
    if (search) {
      const q = search.toLowerCase();
      return `${a.application_code} ${a.legal_name||""} ${a.trade_name||""} ${a.primary_contact_email||""}`.toLowerCase().includes(q);
    }
    return true;
  }), [apps, tab, search]);

  const counts = useMemo(() => {
    const c = {all: apps.length};
    for (const a of apps) c[a.status] = (c[a.status] || 0) + 1;
    return c;
  }, [apps]);

  return (
    <div className="p-6 space-y-5" data-testid="vendor-apps-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Vendor applications</h1>
          <p className="text-sm text-zinc-500">Self-onboarded vendors waiting for review. {counts.submitted || 0} submitted · {counts.under_review || 0} under review · {counts.approved || 0} approved.</p>
        </div>
        <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="settings-btn">
          <LinkIcon size={16} className="mr-1.5"/> Onboarding link
        </Button>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {TABS.map(t => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
                {t.label} {counts[t.key] ? <span className="ml-1.5 text-xs opacity-70">{counts[t.key]}</span> : null}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input placeholder="Search code, name, email…" value={search} onChange={e=>setSearch(e.target.value)} className="w-72" data-testid="search-input"/>
      </div>

      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left px-4 py-3">Code</th><th className="text-left">Vendor</th><th className="text-left">Contact</th><th className="text-left">Submitted</th><th className="text-left">Status</th><th></th></tr>
          </thead>
          <tbody>
            {filtered.map(a => (
              <tr key={a.id} className="border-t hover:bg-zinc-50/50" data-testid={`row-${a.application_code}`}>
                <td className="px-4 py-3 font-mono text-xs">{a.application_code}</td>
                <td>{a.legal_name || a.trade_name || "—"}<div className="text-xs text-zinc-500">{(a.business_categories||[]).slice(0,2).join(" · ")}</div></td>
                <td className="text-xs">{a.primary_contact_email}<div className="text-zinc-500">{a.primary_contact_phone}</div></td>
                <td className="text-xs text-zinc-600">{a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : "—"}</td>
                <td><Badge className={STATUS_LABELS[a.status]?.cls || "bg-zinc-100"}>{STATUS_LABELS[a.status]?.label || a.status}</Badge></td>
                <td className="text-right px-4 py-3"><Button size="sm" variant="outline" onClick={()=>setActive(a)} data-testid={`open-${a.application_code}`}>Open</Button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-500 text-sm py-12">No applications match this filter.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && <ReviewDrawer app={active} onClose={()=>setActive(null)} onDone={()=>{ setActive(null); load(); }}/>}
      {showSettings && settings && <SettingsDrawer s={settings} onClose={()=>setShowSettings(false)} onChanged={loadSettings}/>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────

function ReviewDrawer({ app, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [showClarify, setShowClarify] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [note, setNote] = useState("");

  const decide = async (decision, payload = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/vendor-apps/${app.id}/decide`, { decision, ...payload });
      toast.success(`Application ${decision}d`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText size={18}/> {app.application_code} · {app.legal_name || app.trade_name}
            <Badge className={STATUS_LABELS[app.status]?.cls || "bg-zinc-100"}>{STATUS_LABELS[app.status]?.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 text-sm">
          <Block title="Basic">
            <KV k="Legal name" v={app.legal_name}/>
            <KV k="Trade name" v={app.trade_name}/>
            <KV k="Entity type" v={app.entity_type}/>
            <KV k="Year established" v={app.year_established}/>
            <KV k="Website" v={app.website}/>
            <KV k="Employees" v={app.employee_count_range}/>
          </Block>
          <Block title="Tax & registration">
            <KV k="PAN" v={app.pan}/>
            <KV k="GSTIN" v={app.gstin}/>
            <KV k="GST state" v={app.gst_state_code}/>
            <KV k="CIN" v={app.cin}/>
            <KV k="Udyam (MSME)" v={app.udyam_number}/>
            <KV k="MSME category" v={app.msme_category}/>
            <KV k="IEC" v={app.iec_code}/>
            <KV k="FSSAI" v={app.fssai}/>
          </Block>
          <Block title="Contacts">
            <KV k="Primary" v={`${app.primary_contact_name||""} (${app.primary_contact_designation||""}) — ${app.primary_contact_email}, ${app.primary_contact_phone||""}`}/>
            <KV k="Accounts" v={`${app.accounts_contact_name||""} — ${app.accounts_contact_email||""}, ${app.accounts_contact_phone||""}`}/>
            <KV k="Escalation" v={`${app.escalation_contact_name||""} — ${app.escalation_contact_email||""}, ${app.escalation_contact_phone||""}`}/>
          </Block>
          <Block title="Addresses">
            <KV k="Registered" v={fmtAddr(app.registered_address)}/>
            <KV k="Operations" v={fmtAddr(app.operations_address)}/>
          </Block>
          <Block title="Banking">
            <KV k="Account holder" v={app.account_holder_name}/>
            <KV k="Account number" v={app.account_number}/>
            <KV k="IFSC" v={app.ifsc}/>
            <KV k="Bank" v={`${app.bank_name||""} · ${app.bank_branch||""}`}/>
            <KV k="Type" v={app.account_type}/>
            <KV k="UPI" v={app.upi_id}/>
          </Block>
          <Block title="Capabilities">
            <KV k="Categories" v={(app.business_categories||[]).join(", ")}/>
            <KV k="Products / services" v={app.products_services_offered} multiline/>
            <KV k="Annual turnover" v={app.annual_turnover_range}/>
            <KV k="Payment terms" v={app.payment_terms_requested}/>
            <KV k="Key customers" v={(app.key_customers||[]).join(", ")}/>
            <KV k="Locations served" v={(app.locations_served||[]).join(", ")}/>
          </Block>
          <Block title="Compliance">
            <KV k="ISO" v={(app.iso_certifications||[]).join(", ")}/>
            <KV k="Diversity" v={(app.diversity_certifications||[]).join(", ")}/>
            <KV k="Insurance" v={(app.insurance_policies||[]).join(", ")}/>
            <KV k="Blacklisted" v={app.has_been_blacklisted ? `Yes — ${app.blacklist_disclosure||""}` : "No"}/>
            <KV k="Litigation" v={app.litigation_disclosure || "—"}/>
          </Block>
          <Block title="Consents">
            <KV k="MSA" v={app.msa_accepted ? `Accepted ${app.msa_accepted_at||""} from ${app.msa_accepted_ip||""}` : "—"}/>
            <KV k="Code of Conduct" v={app.code_of_conduct_accepted ? "Accepted" : "—"}/>
            <KV k="DPDP consent" v={app.dpdp_consent ? "Yes" : "—"}/>
          </Block>
          {(app.review_log||[]).length > 0 && (
            <Block title="Review history">
              <ul className="space-y-2">
                {app.review_log.map(e => (
                  <li key={e.id} className="text-xs border-l-2 border-zinc-200 pl-3">
                    <strong>{e.actor_name}</strong> · {e.action} <span className="text-zinc-500">{new Date(e.at).toLocaleString()}</span>
                    {e.note && <div className="text-zinc-700 mt-0.5">{e.note}</div>}
                  </li>
                ))}
              </ul>
            </Block>
          )}
        </div>

        <DialogFooter className="border-t pt-4 mt-4">
          {(app.status === "submitted" || app.status === "under_review") && (
            <>
              <Button variant="outline" onClick={()=>setShowClarify(true)} disabled={busy} data-testid="clarify-btn">Ask for clarification</Button>
              <Button variant="outline" onClick={()=>setShowReject(true)} disabled={busy} className="text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="reject-btn">Reject</Button>
              <Button onClick={()=>decide("approve", { note: "Approved" })} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                <ShieldCheck size={16} className="mr-1.5"/> Approve & convert to vendor
              </Button>
            </>
          )}
          {app.status === "approved" && app.converted_vendor_id && (
            <Button variant="outline" onClick={()=>window.open(`/app/procurement/vendors/${app.converted_vendor_id}`, "_blank")}>
              <ExternalLink size={16} className="mr-1.5"/> Open vendor record
            </Button>
          )}
        </DialogFooter>

        {/* Clarify modal */}
        <Dialog open={showClarify} onOpenChange={(v)=>!v && setShowClarify(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Ask for clarification</DialogTitle></DialogHeader>
            <p className="text-sm text-zinc-600">The vendor will get an email with the questions below and a link to re-open their application.</p>
            <Textarea rows={5} placeholder="Please share a copy of your cancelled cheque and confirm the GSTIN registered name…" value={note} onChange={e=>setNote(e.target.value)} data-testid="clarify-note"/>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setShowClarify(false)}>Cancel</Button>
              <Button onClick={()=>{ setShowClarify(false); decide("clarify", { clarification_request: note }); setNote(""); }} disabled={!note.trim()} data-testid="send-clarify">Send</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reject modal */}
        <Dialog open={showReject} onOpenChange={(v)=>!v && setShowReject(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Reject application</DialogTitle></DialogHeader>
            <Textarea rows={4} placeholder="Reason (will be emailed to the vendor)" value={note} onChange={e=>setNote(e.target.value)} data-testid="reject-note"/>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setShowReject(false)}>Cancel</Button>
              <Button onClick={()=>{ setShowReject(false); decide("reject", { note }); setNote(""); }} disabled={!note.trim()} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reject">Reject</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────────────

function SettingsDrawer({ s, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [inv, setInv] = useState({ name: "", email: "", note: "" });
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/companies/me/vendor-onboarding/invites").then(r => setRecent(r.data || [])).catch(() => {});
  }, []);

  const copy = () => {
    navigator.clipboard.writeText(s.public_url);
    toast.success("Link copied");
  };
  const rotate = async () => {
    if (!window.confirm("Rotating invalidates the existing link immediately. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/companies/me/vendor-onboarding/rotate-slug");
      toast.success("New link generated");
      onChanged();
    } catch (e) { toast.error("Could not rotate"); }
    finally { setBusy(false); }
  };

  const sendInvite = async () => {
    if (!inv.name.trim()) return toast.error("Vendor name is required");
    if (!inv.email.trim() || !inv.email.includes("@")) return toast.error("Valid email is required");
    setSending(true);
    try {
      await api.post("/companies/me/vendor-onboarding/invite", {
        name: inv.name.trim(), email: inv.email.trim(), note: inv.note.trim(),
      });
      toast.success(`Invite sent to ${inv.email.trim()}`);
      setInv({ name: "", email: "", note: "" });
      const r = await api.get("/companies/me/vendor-onboarding/invites");
      setRecent(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v)=>!v && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><LinkIcon size={18}/> Vendor onboarding link</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div className="bg-zinc-50 border rounded-lg p-4">
            <Label className="text-xs text-zinc-500">Your public link — share with vendors via email, WhatsApp or QR code</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={s.public_url} readOnly className="font-mono text-sm" data-testid="public-url"/>
              <Button variant="outline" onClick={copy} data-testid="copy-link"><Copy size={14}/></Button>
              <Button variant="outline" onClick={()=>window.open(s.public_url, "_blank")} data-testid="open-link"><ExternalLink size={14}/></Button>
            </div>
          </div>

          {/* ── Email invite ───────────────────────────────────────── */}
          <div className="border rounded-lg p-4 bg-emerald-50/30">
            <h4 className="font-semibold text-sm text-zinc-900 mb-1">📧 Email the link directly to a vendor</h4>
            <p className="text-xs text-zinc-600 mb-3">They get a personalised invite from you with the onboarding link — no manual copy-paste.</p>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="text-xs">Vendor / contact name *</Label>
                <Input value={inv.name} onChange={e=>setInv({...inv, name: e.target.value})} placeholder="Ramesh Patel" data-testid="invite-name"/>
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input type="email" value={inv.email} onChange={e=>setInv({...inv, email: e.target.value})} placeholder="ramesh@acmevendors.in" data-testid="invite-email"/>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Personal note (optional)</Label>
                <Textarea rows={2} value={inv.note} onChange={e=>setInv({...inv, note: e.target.value})} placeholder="Hi Ramesh — as discussed, please submit your onboarding details by Friday so we can include you in the Q3 RFQs." data-testid="invite-note"/>
              </div>
            </div>
            <Button onClick={sendInvite} disabled={sending} className="mt-3 bg-emerald-600 hover:bg-emerald-700" data-testid="invite-send">
              {sending ? "Sending…" : "Send invite"}
            </Button>
          </div>

          {recent.length > 0 && (
            <div>
              <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Recent invites</h4>
              <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                {recent.slice(0, 10).map(r => (
                  <li key={r.id} className="text-xs flex items-center justify-between border-b last:border-0 pb-1.5">
                    <span><strong className="text-zinc-800">{r.name}</strong> · {r.email}</span>
                    <span className="text-zinc-500">{new Date(r.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-xs text-zinc-600 space-y-1">
            <p>· Anyone with this link can apply; emails are OTP-verified before submission.</p>
            <p>· Rate-limited to 5 applications/IP/hour.</p>
            <p>· Rotate the link if you suspect leak — the old one stops working instantly.</p>
          </div>
          <Button variant="outline" onClick={rotate} disabled={busy} className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="rotate-slug">
            <RefreshCw size={14} className="mr-1.5"/> {busy ? "Rotating…" : "Rotate link"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">{title}</h4>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">{children}</div>
    </div>
  );
}
function KV({ k, v, multiline = false }) {
  return (
    <div className={multiline ? "sm:col-span-2" : ""}>
      <span className="text-xs text-zinc-500">{k}</span>
      <div className={`text-zinc-800 ${multiline ? "whitespace-pre-wrap" : "truncate"}`}>{v || "—"}</div>
    </div>
  );
}
function fmtAddr(a) {
  if (!a) return "—";
  return [a.line1, a.line2, a.city, a.state, a.pincode, a.country].filter(Boolean).join(", ");
}
